import { useEffect, useRef, useState, type ReactNode } from "react";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button, Checkbox, Input } from "@akuna/ui";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";

export interface ExpandedShellMode {
  value: "fields" | "free";
  onChange: (value: "fields" | "free") => void;
  labels: { fields: string; fieldsHint: string; free: string; freeHint: string };
}

/** The card that takes the place of "+ Adicionar" (or of the item being edited). */
export function ExpandedItemShell({
  title,
  ordinal,
  mode,
  onCancel,
  onConfirm,
  confirmLabel,
  saveAsModel,
  dirty,
  mobile = false,
  confirmDisabled = false,
  aboveMode,
  children,
}: {
  title: string;
  ordinal: number;
  mode?: ExpandedShellMode;
  onCancel: () => void;
  onConfirm: () => void;
  confirmLabel: string;
  saveAsModel?: { checked: boolean; name: string; onCheckedChange: (v: boolean) => void; onNameChange: (v: string) => void; label: string };
  dirty: boolean;
  mobile?: boolean;
  confirmDisabled?: boolean;
  /** Rendered between the title and the Fill-in/Write-freely tabs (e.g. the saved-model picker). */
  aboveMode?: ReactNode;
  children: ReactNode;
}) {
  const { t } = useTranslation("prescriptions");
  const ref = useRef<HTMLDivElement>(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    node.scrollIntoView({ block: mobile ? "start" : "nearest", behavior: "smooth" });
  }, [mobile]);

  const requestCancel = () => (dirty ? setConfirmDiscard(true) : onCancel());

  return (
    <div
      ref={ref}
      role="region"
      aria-label={title}
      className={cn(
        "flex scroll-mt-20 flex-col border-2 border-primary bg-card shadow-md",
        mobile ? "gap-3.5 rounded-[18px] p-3.5" : "gap-4 rounded-2xl p-[18px]",
      )}
    >
      <div className="flex items-center gap-2.5">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary text-[13px] font-bold text-primary-foreground">
          {ordinal}
        </span>
        <h4 className="flex-1 font-display text-[17px] font-bold">{title}</h4>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={cn("rounded-[9px] bg-secondary", mobile ? "h-11 w-11" : "h-[34px] w-[34px]")}
          aria-label={t("editor.shell.close")}
          onClick={requestCancel}
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </Button>
      </div>

      {aboveMode}

      {mode ? (
        <div role="tablist" className="grid grid-cols-2 gap-2.5">
          {(["fields", "free"] as const).map((value) => {
            const active = mode.value === value;
            return (
              <button
                key={value}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => mode.onChange(value)}
                className={cn(
                  "rounded-xl px-3.5 py-2.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  active ? "border-2 border-primary bg-accent" : "border border-border",
                )}
              >
                <div className="text-[14.5px] font-semibold">{value === "fields" ? mode.labels.fields : mode.labels.free}</div>
                {!mobile ? (
                  <div className="text-xs text-muted-foreground">{value === "fields" ? mode.labels.fieldsHint : mode.labels.freeHint}</div>
                ) : null}
              </button>
            );
          })}
        </div>
      ) : null}

      {children}

      <div className={cn("flex border-t pt-4", mobile ? "flex-col gap-3" : "items-center gap-2.5")}>
        {saveAsModel ? (
          <div className={cn("flex items-center gap-2", !mobile && "mr-auto")}>
            <Checkbox
              id={`save-model-${ordinal}`}
              checked={saveAsModel.checked}
              onCheckedChange={(checked) => saveAsModel.onCheckedChange(checked === true)}
            />
            <Label htmlFor={`save-model-${ordinal}`} className="text-[13px]">
              {saveAsModel.label}
            </Label>
            {saveAsModel.checked ? (
              <Input
                value={saveAsModel.name}
                onChange={(event) => saveAsModel.onNameChange(event.target.value)}
                placeholder={t("editor.shell.modelNamePlaceholder")}
                aria-label={t("editor.shell.modelNamePlaceholder")}
                className="h-9 w-48"
              />
            ) : null}
          </div>
        ) : (
          <span className="mr-auto" />
        )}
        <div className={cn("flex gap-2.5", mobile && "w-full")}>
          <Button type="button" variant="outline" className={cn(mobile ? "h-[52px] flex-1" : "h-11")} onClick={requestCancel}>
            {t("editor.shell.cancel")}
          </Button>
          <Button type="button" className={cn(mobile ? "h-[52px] flex-1" : "h-11")} onClick={onConfirm} disabled={confirmDisabled}>
            {confirmLabel}
          </Button>
        </div>
      </div>

      <AlertDialog open={confirmDiscard} onOpenChange={setConfirmDiscard}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("editor.shell.discardTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("editor.shell.discardDescription")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("editor.shell.keepEditing")}</AlertDialogCancel>
            <AlertDialogAction onClick={onCancel}>{t("editor.shell.discard")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
